import React, { useState, useEffect } from 'react';
import { auth, database, storage } from '../../firebaseConfig';
import { ref as dbRef, update, remove } from 'firebase/database';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import VideoPlayer from './VideoPlayer';
import './VideoManagementModal.css';

function VideoManagementModal({ category, videos, categories, onClose, onVideosUpdated }) {
    const [categoryVideos, setCategoryVideos] = useState([]);
    const [editingVideo, setEditingVideo] = useState(null);
    const [playingVideo, setPlayingVideo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);

    useEffect(() => {
        // Filter videos for this category and add order if not exists
        const filteredVideos = videos
            .filter(video => video.categoryId === category.id)
            .map((video, index) => ({
                ...video,
                order: video.order !== undefined ? video.order : index
            }))
            .sort((a, b) => a.order - b.order);
        
        setCategoryVideos(filteredVideos);
    }, [videos, category.id]);

    const handleDragEnd = async (result) => {
        if (!result.destination) return;

        const items = Array.from(categoryVideos);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Update order for all videos
        const updatedVideos = items.map((video, index) => ({
            ...video,
            order: index
        }));

        setCategoryVideos(updatedVideos);

        // Save new order to database
        try {
            const updates = {};
            updatedVideos.forEach((video) => {
                updates[`Academy/videos/${video.id}/order`] = video.order;
            });

            await update(dbRef(database), updates);
            onVideosUpdated();
        } catch (error) {
            console.error('Error updating video order:', error);
            setError('Failed to update video order');
        }
    };

    const handleDeleteVideo = async (videoId) => {
        if (!window.confirm('Are you sure you want to delete this video?')) return;

        setLoading(true);
        try {
            await remove(dbRef(database, `Academy/videos/${videoId}`));
            setCategoryVideos(prev => prev.filter(v => v.id !== videoId));
            onVideosUpdated();
        } catch (error) {
            console.error('Error deleting video:', error);
            setError('Failed to delete video');
        } finally {
            setLoading(false);
        }
    };

    const handleEditVideo = (video) => {
        setEditingVideo({
            ...video,
            title: video.title || '',
            description: video.description || '',
            categoryId: video.categoryId
        });
        // Reset thumbnail states
        setThumbnailFile(null);
        setThumbnailPreview(null);
        setUploadProgress(0);
    };

    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select a valid image file');
            return;
        }

        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
            setError('Image size must be less than 5MB');
            return;
        }

        setThumbnailFile(file);
        
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
            setThumbnailPreview(e.target.result);
        };
        reader.readAsDataURL(file);
        setError('');
    };

    const removeThumbnail = () => {
        setThumbnailFile(null);
        setThumbnailPreview(null);
        setUploadProgress(0);
    };

    const uploadThumbnail = async () => {
        if (!thumbnailFile) return null;

        const thumbnailRef = storageRef(storage, `academy/thumbnails/${auth.currentUser.uid}/${Date.now()}_${thumbnailFile.name}`);
        
        return new Promise((resolve, reject) => {
            const uploadTask = uploadBytesResumable(thumbnailRef, thumbnailFile);
            
            uploadTask.on('state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(progress);
                },
                (error) => {
                    console.error('Thumbnail upload error:', error);
                    reject(error);
                },
                async () => {
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        resolve(downloadURL);
                    } catch (error) {
                        reject(error);
                    }
                }
            );
        });
    };

    const handleSaveEdit = async () => {
        if (!editingVideo || !editingVideo.title.trim()) {
            setError('Video title is required');
            return;
        }

        setLoading(true);
        try {
            let thumbnailUrl = editingVideo.thumbnailUrl;

            // Upload new thumbnail if one was selected
            if (thumbnailFile) {
                try {
                    thumbnailUrl = await uploadThumbnail();
                    
                    // Delete old thumbnail if it exists and is different
                    if (editingVideo.thumbnailUrl && editingVideo.thumbnailUrl !== thumbnailUrl) {
                        try {
                            const oldThumbnailRef = storageRef(storage, editingVideo.thumbnailUrl);
                            await deleteObject(oldThumbnailRef);
                        } catch (deleteError) {
                            console.warn('Could not delete old thumbnail:', deleteError);
                        }
                    }
                } catch (uploadError) {
                    console.error('Thumbnail upload failed:', uploadError);
                    setError('Failed to upload thumbnail');
                    setLoading(false);
                    return;
                }
            }

            const updates = {
                title: editingVideo.title.trim(),
                description: editingVideo.description.trim(),
                categoryId: editingVideo.categoryId,
                thumbnailUrl: thumbnailUrl,
                updatedAt: new Date().toISOString()
            };

            await update(dbRef(database, `Academy/videos/${editingVideo.id}`), updates);
            
            // Update local state
            setCategoryVideos(prev => 
                prev.map(video => 
                    video.id === editingVideo.id 
                        ? { ...video, ...updates }
                        : video
                )
            );

            setEditingVideo(null);
            setThumbnailFile(null);
            setThumbnailPreview(null);
            setUploadProgress(0);
            onVideosUpdated();
        } catch (error) {
            console.error('Error updating video:', error);
            setError('Failed to update video');
        } finally {
            setLoading(false);
        }
    };

    const formatDuration = (seconds) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return 'Unknown';
        return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    };

    return (
        <div className="academy-video-management-overlay">
            <div className="academy-video-management-modal">
                <div className="academy-modal-header">
                    <h2>Manage Videos - {category.name}</h2>
                    <button className="academy-close-btn" onClick={onClose}>✕</button>
                </div>

                {error && (
                    <div className="academy-error-message">
                        {error}
                        <button onClick={() => setError('')}>✕</button>
                    </div>
                )}

                <div className="academy-modal-content">
                    {categoryVideos.length === 0 ? (
                        <div className="academy-empty-state">
                            <p>No videos in this category yet.</p>
                        </div>
                    ) : (
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="videos">
                                {(provided) => (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className="academy-videos-list"
                                    >
                                        {categoryVideos.map((video, index) => (
                                            <Draggable
                                                key={video.id}
                                                draggableId={video.id}
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        className={`academy-video-item ${snapshot.isDragging ? 'dragging' : ''}`}
                                                    >
                                                        <div 
                                                            {...provided.dragHandleProps}
                                                            className="academy-drag-handle"
                                                        >
                                                            ⋮⋮
                                                        </div>

                                                        <div className="academy-video-thumbnail">
                                                            {video.thumbnailUrl ? (
                                                                <img 
                                                                    src={video.thumbnailUrl} 
                                                                    alt={video.title}
                                                                    onClick={() => setPlayingVideo(video)}
                                                                />
                                                            ) : (
                                                                <div 
                                                                    className="academy-thumbnail-placeholder"
                                                                    onClick={() => setPlayingVideo(video)}
                                                                >
                                                                    📹
                                                                </div>
                                                            )}
                                                            <button 
                                                                className="academy-play-overlay"
                                                                onClick={() => setPlayingVideo(video)}
                                                            >
                                                                ▶️
                                                            </button>
                                                        </div>

                                                        <div className="academy-video-info">
                                                            <h4>{video.title}</h4>
                                                            <p>{video.description || 'No description'}</p>
                                                            <div className="academy-video-metadata">
                                                                {video.videoMetadata?.duration && (
                                                                    <span>Duration: {formatDuration(video.videoMetadata.duration)}</span>
                                                                )}
                                                                {video.videoMetadata?.fileSize && (
                                                                    <span>Size: {formatFileSize(video.videoMetadata.fileSize)}</span>
                                                                )}
                                                                {video.videoMetadata?.width && video.videoMetadata?.height && (
                                                                    <span>Resolution: {video.videoMetadata.width}x{video.videoMetadata.height}</span>
                                                                )}
                                                                <span>Source: {video.videoSource === 'upload' ? 'Uploaded' : 'URL'}</span>
                                                                <span>👁 Views: {video.viewCount || 0}</span>
                                                            </div>
                                                        </div>

                                                        <div className="academy-video-actions">
                                                            <button
                                                                className="academy-action-btn academy-edit-btn"
                                                                onClick={() => handleEditVideo(video)}
                                                                disabled={loading}
                                                            >
                                                                ✏️ Edit
                                                            </button>
                                                            <button
                                                                className="academy-action-btn academy-play-btn"
                                                                onClick={() => setPlayingVideo(video)}
                                                            >
                                                                ▶️ Play
                                                            </button>
                                                            <button
                                                                className="academy-action-btn academy-delete-btn"
                                                                onClick={() => handleDeleteVideo(video.id)}
                                                                disabled={loading}
                                                            >
                                                                🗑️ Delete
                                                            </button>
                                                        </div>

                                                        <div className="academy-order-indicator">
                                                            #{index + 1}
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}
                </div>

                <div className="academy-modal-footer">
                    <p>💡 Drag videos to reorder them. Changes are saved automatically.</p>
                    <button className="academy-close-modal-btn" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>

            {/* Edit Video Modal */}
            {editingVideo && (
                <div className="academy-edit-video-overlay">
                    <div className="academy-edit-video-modal">
                        <div className="academy-edit-header">
                            <h3>Edit Video</h3>
                            <button onClick={() => setEditingVideo(null)}>✕</button>
                        </div>

                        <div className="academy-edit-form">
                            <div className="academy-form-group">
                                <label>Title *</label>
                                <input
                                    type="text"
                                    value={editingVideo.title}
                                    onChange={(e) => setEditingVideo(prev => ({
                                        ...prev,
                                        title: e.target.value
                                    }))}
                                    maxLength={100}
                                />
                            </div>

                            <div className="academy-form-group">
                                <label>Description</label>
                                <textarea
                                    value={editingVideo.description}
                                    onChange={(e) => setEditingVideo(prev => ({
                                        ...prev,
                                        description: e.target.value
                                    }))}
                                    rows={6}
                                />
                            </div>

                            <div className="academy-form-group">
                                <label>Category</label>
                                <select
                                    value={editingVideo.categoryId}
                                    onChange={(e) => setEditingVideo(prev => ({
                                        ...prev,
                                        categoryId: e.target.value
                                    }))}
                                >
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="academy-form-group">
                                <label>Video Thumbnail</label>
                                <div className="academy-thumbnail-upload-section">
                                    {/* Current thumbnail display */}
                                    {(editingVideo.thumbnailUrl || thumbnailPreview) && (
                                        <div className="academy-current-thumbnail">
                                            <img 
                                                src={thumbnailPreview || editingVideo.thumbnailUrl} 
                                                alt="Current thumbnail" 
                                                className="academy-thumbnail-preview"
                                            />
                                            <div className="academy-thumbnail-info">
                                                {thumbnailPreview ? 'New thumbnail selected' : 'Current thumbnail'}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Upload area */}
                                    <div className="academy-thumbnail-upload-area">
                                        <input
                                            type="file"
                                            id="thumbnail-upload"
                                            accept="image/*"
                                            onChange={handleThumbnailChange}
                                            className="academy-thumbnail-input"
                                            disabled={loading}
                                        />
                                        <label htmlFor="thumbnail-upload" className="academy-thumbnail-upload-label">
                                            <div className="academy-upload-icon">📁</div>
                                            <div className="academy-upload-text">
                                                {editingVideo.thumbnailUrl ? 'Change Thumbnail' : 'Add Thumbnail'}
                                            </div>
                                            <small>JPG, PNG up to 5MB</small>
                                        </label>
                                        
                                        {thumbnailFile && (
                                            <button
                                                type="button"
                                                onClick={removeThumbnail}
                                                className="academy-remove-thumbnail-btn"
                                                disabled={loading}
                                            >
                                                ✕ Remove
                                            </button>
                                        )}
                                    </div>

                                    {/* Upload progress */}
                                    {uploadProgress > 0 && uploadProgress < 100 && (
                                        <div className="academy-upload-progress">
                                            <div className="academy-progress-bar">
                                                <div 
                                                    className="academy-progress-fill" 
                                                    style={{ width: `${uploadProgress}%` }}
                                                ></div>
                                            </div>
                                            <span className="academy-progress-text">{Math.round(uploadProgress)}%</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                        
                        <div className="academy-edit-actions">
                            <button 
                                onClick={() => setEditingVideo(null)}
                                className="academy-cancel-btn"
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleSaveEdit}
                                className="academy-save-btn"
                                disabled={loading || !editingVideo.title.trim()}
                            >
                                {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Video Player Modal */}
            {playingVideo && (
                <VideoPlayer
                    video={playingVideo}
                    onClose={() => setPlayingVideo(null)}
                />
            )}
        </div>
    );
}

export default VideoManagementModal;
